<script lang="ts">
import { markRaw } from 'vue'
import WMTSCapabilities from 'ol/format/WMTSCapabilities'
import { optionsFromCapabilities } from 'ol/source/WMTS'
import { formatUrlToRequest } from '../../../utils/map/MapServiceHelper'
import { normalizeCrs, tileLoadAuthentification } from '../../../utils/map/OlLayerHelper'

// Géoplateforme capabilities weigh ~3 MB, come without cache headers and are shared by every layer of the service.
const capabilitiesCache = new Map<string, Promise<any>>()

function fetchCapabilities(url: string): Promise<any> {
    let pending = capabilitiesCache.get(url)
    if (!pending) {
        pending = $fetch(url).then((res) => {
            if (!res) {
                throw new Error(`[WMTSLayer] Empty capabilities response for "${url}"`)
            }
            return markRaw(new WMTSCapabilities().read(res))
        })
        pending.catch(() => capabilitiesCache.delete(url))
        capabilitiesCache.set(url, pending)
    }
    return pending
}

export default {
    name: 'WMTSLayer',
    props: {
        layer: { type: Object, required: true },
    },
    data() {
        return {
            serviceCapabilities: null as any,
            sourceOptions: null as any,
        }
    },
    mounted() {
        this.getUrlService()
            .then((res) => {
                this.serviceCapabilities = res
                this.sourceOptions = this.buildSourceOptions()
            })
            .catch((err) => {
                console.error('[WMTSLayer] Failed to load capabilities:', err)
            })
    },
    methods: {
        async getUrlService(): Promise<any> {
            const capabilitiesUrl = this.layer.url ?? this.layer.service?.url
            if (!capabilitiesUrl) {
                throw new Error(`[WMTSLayer] layer "${this.layer.name}" has no url nor service.url`)
            }
            return fetchCapabilities(formatUrlToRequest(capabilitiesUrl, this.layer.type))
        },
        buildSourceOptions() {
            const matrixSet = this.getPreferredMatrixSet()
            if (!matrixSet) {
                console.error(`[WMTSLayer] No TileMatrixSet found for layer="${this.layer.name}"`)
                return null
            }
            const projection = this.normalizeCrs(this.getCrsForMatrixSet(matrixSet))
            // optionsFromCapabilities reads the exact tile grid (origin, resolutions, tile size)
            // defined by the server, preventing TileOutOfRange errors from grid mismatch.
            const options = optionsFromCapabilities(this.serviceCapabilities, {
                layer: this.layer.name,
                matrixSet,
                projection,
            })
            if (!options) {
                console.error(`[WMTSLayer] optionsFromCapabilities returned null for layer="${this.layer.name}", matrixSet="${matrixSet}"`)
                return null
            }
            // For KVP layers: keep layer.service.url (backward compatibility with existing layers).
            // For REST layers: use the template URL from capabilities ({TileMatrix}/{TileRow}/{TileCol}).
            const isRest = options.requestEncoding === 'REST'
            return {
                ...options,
                urls: (!isRest && this.layer.service?.url) ? [this.layer.service.url] : options.urls,
            }
        },
        getPreferredMatrixSet(): string | undefined {
            if (this.layer.tileMatrix) return this.layer.tileMatrix
            const layerInfo = this.serviceCapabilities.Contents.Layer.find((l: any) => l.Identifier === this.layer.name)
            const links: { TileMatrixSet: string }[] = layerInfo?.TileMatrixSetLink ?? []
            if (!links.length) return undefined
            const preferred = links.find(l => this.normalizeCrs(this.getCrsForMatrixSet(l.TileMatrixSet)) === 'EPSG:3857')
            return (preferred ?? links[0]).TileMatrixSet
        },
        getCrsForMatrixSet(matrixSetId: string): string {
            const tms = this.serviceCapabilities?.Contents?.TileMatrixSet?.find(
                (t: any) => t.Identifier === matrixSetId
            )
            return tms?.SupportedCRS ?? matrixSetId
        },
        normalizeCrs,
        getTileAuthentification(tile: any, src: string) {
            tileLoadAuthentification(tile, src, this.layer, 'WMTS')
        },
    },
}
</script>

<template>
    <ol-tile-layer
        :title="layer.title" :visible="layer.show" :class-name="layer.name" :properties="{ ...layer }"
        :z-index="layer.zIndex"
    >
        <ol-source-wmts
            v-if="sourceOptions"
            :ref="layer.id ?? layer.title"
            :url="sourceOptions.urls[0]"
            :layer="sourceOptions.layer"
            :matrix-set="sourceOptions.matrixSet"
            :format="sourceOptions.format"
            :styles="sourceOptions.style"
            :projection="sourceOptions.projection?.getCode?.() ?? sourceOptions.projection"
            :tile-grid="sourceOptions.tileGrid"
            :request-encoding="sourceOptions.requestEncoding"
            :tileLoadFunction="getTileAuthentification"
            :attributions="layer.attribution"
            crossOrigin="anonymous"
        />
    </ol-tile-layer>
</template>

<style scoped>

</style>
